import { useCallback, useMemo } from "react"
import _, { compact, get, orderBy, uniqBy } from "lodash"
import { captureException } from "@sentry/react"

import { FindRouteOptions, NavigatableParam } from "venue-js"
import { NavigatableFeature, IRoute } from "../../type"
import {
  FeaturePropType,
  UseShortestPathOptions,
  UseShortestPathResult,
} from "../shared/types"
import { LevelData } from "providers/venue/types"

import {
  createArrivalVenueSDKStep,
  flattenStepByInstructions,
  normalizePlaceName,
  normalizeTurnName,
  normalizeUnitDistanceOptions,
  transformStepsToGeometriesVenueSDK,
} from "./utils"
import { useVenueDataClient } from "venue-react"

export const useShortestPathVenueSDK = (
  { featuresObj = {} }: FeaturePropType,
  {
    enabled = true,
    graphOptions,
    include_instructions: includeInstructions = false,
    resolution,
    debug,
  }: UseShortestPathOptions
): UseShortestPathResult => {
  const { navigate } = useVenueDataClient()

  const normalizedUnitDistanceOptions = useMemo(
    () => normalizeUnitDistanceOptions(graphOptions),
    [graphOptions]
  )

  const calculateRoute = useCallback(
    async (
      routeOrigin: NavigatableFeature,
      routeDestination: NavigatableFeature,
      options: FindRouteOptions = {}
    ): Promise<IRoute | null> => {
      // If relationshipGraph is not loaded, return null
      if (!enabled || !routeOrigin || !routeDestination || !navigate)
        return null

      try {
        const { distance, duration, steps } = await navigate(
          routeOrigin?.id as NavigatableParam,
          routeDestination?.id as NavigatableParam,
          {
            ...(options ?? {}),
            includeInstructions,
            unitDistanceOptions: normalizedUnitDistanceOptions,
            walkablePath: { resolution, debug },
          }
        )

        if (!steps?.length) throw new Error(`Error: Unable to find route.`)
        return {
          origin: routeOrigin,
          destination: routeDestination,
          description: null,
          distance,
          duration,
          steps,
        }
      } catch (error) {
        console.log(error)
        captureException(error, (scope) => {
          scope.setTransactionName(`Error: Unable to find route.`)
          return scope
        })
        throw error
      }
    },
    [
      enabled,
      includeInstructions,
      normalizedUnitDistanceOptions,
      resolution,
      debug,
      navigate,
    ]
  )

  const findRoute = useCallback(
    async (
      routeOrigin: NavigatableFeature,
      routeDestination: NavigatableFeature
    ): Promise<IRoute> => {
      return await calculateRoute(routeOrigin, routeDestination)
    },
    [calculateRoute]
  )

  const findElevatorPreferredRoute = async (
    routeOrigin: NavigatableFeature,
    routeDestination: NavigatableFeature
  ): Promise<IRoute> => {
    return await calculateRoute(routeOrigin, routeDestination, {
      mode: "accessible",
    })
  }

  const getRelatedLevelWithSteps = useCallback(
    (steps = []): LevelData[] => {
      const levels = steps.reduce((acc, step) => {
        const levelIds = get(step, "levelIds", [])
        const levelFeatures = compact(
          levelIds.map((id) => get(featuresObj, id))
        )
        return [...acc, ...levelFeatures]
      }, [])
      const sortedLevels = orderBy(levels, ["properties.ordinal"], ["desc"])
      return uniqBy(sortedLevels, "properties.ordinal")
    },
    [featuresObj]
  )

  const normalizeStepInRouteVenueSDK = useCallback(
    (route) => {
      const steps = get(route, "steps", [])
      if (!Array.isArray(steps) || steps.length === 0) return null

      const normalizedSteps = steps.flatMap((step) =>
        flattenStepByInstructions(step).map((flattenedStep) => {
          const { id: originId, ...originProps } = get(
            flattenedStep,
            "from",
            {}
          )
          const { id: destinationId, ...destinationProps } = get(
            flattenedStep,
            "to",
            {}
          )
          const originLevelId = get(originProps, "levelId")
          const origin = {
            id: originId,
            properties: {
              ...originProps,
              level: get(featuresObj, originLevelId),
            },
          }

          const destinationLevelId = get(destinationProps, "levelId")
          const destination = {
            id: destinationId,
            properties: {
              ...destinationProps,
              level: get(featuresObj, destinationLevelId),
            },
          }
          return {
            ...flattenedStep,
            origin,
            destination,
            description: {
              ...(flattenedStep.description ?? {}),
              text: flattenedStep.description.template,
              options: {
                ...(flattenedStep.description?.options ?? {}),
                intermediary: normalizePlaceName(
                  flattenedStep.description?.options?.intermediary
                ),
                toward: normalizePlaceName(
                  flattenedStep.description?.options?.toward
                ),
                landmark: normalizePlaceName(
                  flattenedStep.description?.options?.landmark
                ),
                turn: normalizeTurnName(
                  flattenedStep.description?.options?.turn
                ),
              },
            },
            path: flattenedStep.path.map((coords) => {
              const [lng, lat] = coords
              return [lng, lat, 0]
            }),
          }
        })
      )

      normalizedSteps.push(createArrivalVenueSDKStep(normalizedSteps))
      return {
        ...route,
        steps: normalizedSteps,
      }
    },
    [featuresObj]
  )
  return {
    normalizeStepInRoute: normalizeStepInRouteVenueSDK,
    relationshipGraphLoaded: enabled,
    findRoute: _.memoize(
      findRoute,
      (origin, destination) => `${origin.id} ${destination.id}`
    ),
    findElevatorPreferredRoute: _.memoize(
      findElevatorPreferredRoute,
      (origin, destination) => `${origin.id} ${destination.id}`
    ),
    getRelatedLevelWithSteps,
    transformStepsToGeometries: transformStepsToGeometriesVenueSDK,
  }
}
